/**
 * Per-user Claude Code profile directories.
 *
 * When a guest (not the host owner) runs a Claude Code agent, their session must
 * run under their own Claude account *and* keep its state (history, `.claude.json`,
 * sessions) separate from the owner's. Authentication is supplied at spawn time as
 * a `CLAUDE_CODE_OAUTH_TOKEN` env var (see `claude-token-store.ts`); this module
 * handles the *state* isolation by giving each guest a dedicated
 * `CLAUDE_CONFIG_DIR` (`~/.claude-{key}/`) that symlinks the owner's non-auth
 * config (settings, hooks, skills, …) back in but keeps everything else isolated.
 *
 * The `key` is the connecting user's *verified* userId — never a self-declared
 * username.
 */

import { existsSync, mkdirSync, symlinkSync } from "node:fs";
import path from "node:path";
import os from "node:os";
import type { Logger } from "pino";

/** Files/dirs that are symlinked from the owner's ~/.claude/ into the profile. */
const SHARED_ITEMS = ["settings.json", "hooks", "agents", "skills", "plugins", "keybindings.json"];

/** Make a verified userId safe to use as a directory suffix. */
function sanitizeKey(key: string): string {
  return key.replace(/[^a-zA-Z0-9_-]/g, "_");
}

/**
 * Return the profile directory path for a given key (verified userId).
 * e.g. key "user_abc123" → "/home/nico/.claude-user_abc123"
 */
export function getClaudeProfileDir(key: string): string {
  return path.join(os.homedir(), `.claude-${sanitizeKey(key)}`);
}

/**
 * Ensure the profile directory exists with the correct symlinks.
 * Safe to call repeatedly — skips already-existing symlinks.
 * Returns the profile directory path.
 */
export function ensureClaudeProfile(key: string, logger?: Logger): string {
  const profileDir = getClaudeProfileDir(key);
  const ownerDir = path.join(os.homedir(), ".claude");

  if (!existsSync(ownerDir)) {
    throw new Error(`Owner claude config dir not found: ${ownerDir}`);
  }

  if (!existsSync(profileDir)) {
    mkdirSync(profileDir, { recursive: true });
    logger?.info({ profileDir, key }, "created claude profile directory");
  }

  for (const item of SHARED_ITEMS) {
    const target = path.join(ownerDir, item);
    const link = path.join(profileDir, item);

    // Only symlink if the source exists in the owner's config
    if (!existsSync(target)) continue;
    // Skip if already exists (symlink or real file)
    if (existsSync(link)) continue;

    symlinkSync(target, link);
    logger?.info({ item, profileDir }, "symlinked shared config item");
  }

  return profileDir;
}
